import React, { useState } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import { login } from "../../utils/auth";
import { useBranding } from "../../contexts/BrandingContext";
import logo from "../../Assets/Images/Logos/lms_logo.png";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); TenantAdminLogin.scss deleted with it.
//
// The page background is the same three-stop 135deg gradient UpgradeBanner already
// writes as one arbitrary value: `surface-dark` is theme-INVARIANT by design (a
// platform-admin door is dark on a light OS and dark on a dark one), and the middle
// stop is a color-mix towards the brand, so an institute that re-themes the console
// re-themes this too.
const PAGE =
  "tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-p-5 tw-font-sans " +
  "tw-bg-[linear-gradient(135deg,hsl(var(--surface-dark))_0%,color-mix(in_srgb,hsl(var(--surface-dark))_60%,hsl(var(--primary)))_50%,hsl(var(--surface-dark))_100%)]";

// `taFadeIn` is gone rather than moved: `slide-up` in tailwind.config.js is the same
// motion (enter from below, fade in) at 20px/0.3s instead of 16px/0.45s, and a
// seventh keyframe for a 4px difference is a second vocabulary, not a conversion.
//
// The shadow keeps its geometry and loses its literal. `rgba(0,0,0,0.3)` was fixed
// in both themes; --popover-shadow is the token for a surface that FLOATS (0.18
// light, 0.55 dark) and is what shadow-popover is built from — but that one is
// 10px/34px, and this card's separation from a full-bleed gradient is the 60px.
const CARD =
  "tw-w-full tw-max-w-[420px] tw-animate-slide-up tw-rounded-lg tw-bg-surface-raised " +
  "tw-px-8 tw-py-10 tw-shadow-[0_20px_60px_var(--popover-shadow)] " +
  "max-[480px]:tw-rounded-md max-[480px]:tw-px-5 max-[480px]:tw-py-8";

const LABEL = "tw-mb-1.5 tw-block tw-text-[0.82rem] tw-font-semibold tw-text-content";

// 1.5px is not on the border scale and is deliberate — it is what separates an
// unfocused field from the card behind it at this size.
const FIELD =
  "tw-flex tw-items-center tw-rounded-md tw-border-[1.5px] tw-border-solid tw-border-line " +
  "tw-bg-[var(--input-bg)] tw-px-3.5 tw-transition-[border-color,box-shadow] tw-duration-200 " +
  "focus-within:tw-border-primary focus-within:tw-ring-[3px] focus-within:tw-ring-primary/10";

const INPUT =
  "tw-flex-1 tw-border-none tw-bg-transparent tw-px-2 tw-py-3 tw-text-[0.92rem] " +
  "tw-text-content tw-outline-none placeholder:tw-text-content-subtle";

const ICON = "tw-shrink-0 tw-text-[0.9rem] tw-text-content-subtle";

// enabled:hover rather than hover:not(:disabled) — same rule, and it composes with
// the disabled: variant below instead of racing it.
const SUBMIT =
  "tw-w-full tw-rounded-md tw-border-none tw-bg-surface-dark tw-p-3 tw-text-[0.95rem] " +
  "tw-font-medium tw-text-on-surface-dark tw-transition-all tw-duration-200 " +
  "enabled:hover:tw-bg-[color-mix(in_srgb,hsl(var(--surface-dark))_88%,white)] " +
  "enabled:hover:tw--translate-y-px disabled:tw-opacity-60";


const TenantAdminLogin = () => {
  const branding = useBranding();
  const brandLogo = branding.logoUrl || logo;
  const brandFavIcon = branding.faviconUrl || branding.logoUrl || logo;
  const brandName = branding.brandName;

  const [creds, setCreds] = useState({ phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setCreds((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.post("/auth/login", {
        ...creds,
        usertype: "tenantadmin",
      });
      if (data.token) {
        login(data);
        navigate("/sa");
      } else {
        setError("Invalid response from server.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Login failed!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={PAGE}>
      <div className={CARD}>
        <div className="tw-mb-8 tw-text-center">
          <img
            src={brandFavIcon}
            alt="Logo"
            className="tw-mb-3 tw-h-14 tw-w-14 tw-object-contain"
          />
          <h1 className="tw-mb-2 tw-text-2xl tw-font-extrabold tw-tracking-[-0.02em] tw-text-content">
            {brandName}
          </h1>
          <span className="tw-mb-2 tw-inline-block tw-rounded-[20px] tw-border tw-border-solid tw-border-primary/25 tw-bg-primary/[0.12] tw-px-3.5 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.05em] tw-text-surface-dark">
            Platform Administration
          </span>
          <p className="tw-m-0 tw-text-[0.9rem] tw-text-content-muted">
            Sign in to the tenant management console
          </p>
        </div>

        <Form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-5">
          <div>
            <label className={LABEL}>Phone Number</label>
            <div className={FIELD}>
              <Mail size="1em" className={ICON} />
              <input
                type="text"
                className={INPUT}
                placeholder="Enter your phone number"
                name="phone"
                autoComplete="username"
                value={creds.phone}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div>
            <label className={LABEL}>Password</label>
            <div className={FIELD}>
              <Lock size="1em" className={ICON} />
              <input
                type={showPassword ? "text" : "password"}
                className={INPUT}
                placeholder="Enter your password"
                name="password"
                autoComplete="current-password"
                value={creds.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="tw-flex tw-cursor-pointer tw-items-center tw-border-none tw-bg-transparent tw-p-1 tw-text-content-subtle hover:tw-text-content"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <Eye size="1em" /> : <EyeOff size="1em" />}
              </button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="tw-m-0 tw-rounded-md tw-text-[0.85rem]">
              {error}
            </Alert>
          )}

          <Button type="submit" className={SUBMIT} disabled={loading}>
            {loading ? <Spinner size="sm" animation="border" /> : "Sign In"}
          </Button>
        </Form>

        <div className="tw-mt-7 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-5 tw-text-center">
          <img src={brandLogo} alt={brandName} className="tw-h-6 tw-opacity-50" />
        </div>
      </div>
    </div>
  );
};

export default TenantAdminLogin;
